import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { GET, TimesheetData } from "~utils";

import { TimesheetEntry } from "../TimesheetRecord";

/**
 * Custom hook for fetching timesheet data by interval ID
 * @param intervalId - The interval ID to fetch timesheet data for
 * @returns Query result with transformed timesheet entries
 */
export function useTimesheet(intervalId: string | undefined) {
  return useQuery({
    enabled: !!intervalId,
    queryKey: ["timesheets", intervalId],
    queryFn: () =>
      GET("/api/sub/timesheet/{interval-id}", { pathParams: { "interval-id": intervalId! } }),
    select: (data: { data: TimesheetData }) => sortTransformTimesheetEntries(data.data),
  });
}

/**
 * Given the raw API response, this will sort all the entries, including the
 * nested `entries` field, and convert all ISO strings to DateTime objects.
 *
 * TODO: Write tests for this.
 *
 * @returns A sorted array where the first entry is the oldest entry.
 */
function sortTransformTimesheetEntries(timesheetEntries: TimesheetData): Array<TimesheetEntry> {
  // Sort the timesheet entries placing the oldest entries at the top.
  const sortedTimesheetEntries = timesheetEntries.toSorted((a, b) => {
    const aTime =
      a.type === "TIMECARD"
        ? DateTime.fromISO(a.date).toMillis()
        : DateTime.fromISO(
            // For PayCode entries, use the date field if available, otherwise
            // use a date in the future so that this entry is sorted to the bottom.
            a.date ?? "2099-01-01",
          ).toMillis();
    const bTime =
      b.type === "TIMECARD"
        ? DateTime.fromISO(b.date).toMillis()
        : DateTime.fromISO(
            // For PayCode entries, use the date field if available, otherwise
            // use a date in the future so that this entry is sorted to the bottom.
            b.date ?? "2099-01-01",
          ).toMillis();
    return aTime - bTime;
  });

  // Then sort all TimeCard entries so that the oldest is at the top
  sortedTimesheetEntries
    .filter((entry) => entry.type === "TIMECARD")
    .forEach((timecard) => {
      timecard.entries.toSorted((a, b) => {
        const aTime = DateTime.fromISO(
          // If a entry is missing a timestamp, set a newer date so that it is
          // sorted to the bottom.
          a.ts ?? "2099-01-01",
        ).toMillis();
        const bTime = DateTime.fromISO(
          // If a entry is missing a timestamp, set a newer date so that it is
          // sorted to the bottom.
          b.ts ?? "2099-01-01",
        ).toMillis();
        return aTime - bTime;
      });
    });

  // Lastly, update each ISO string to be a DateTime object.
  return sortedTimesheetEntries.reduce((acc, record) => {
    if (record.type === "TIMECARD") {
      acc.push({
        ...record,
        date: DateTime.fromISO(record.date),
        request: {
          ...record.request,
          startTime: DateTime.fromISO(record.request.startTime),
          endTime: DateTime.fromISO(record.request.endTime),
        },
        entries: record.entries.map((entry) => {
          return {
            ...entry,
            ts: entry.ts ? DateTime.fromISO(entry.ts) : undefined,
          };
        }),
      });
    } else {
      acc.push({
        ...record,
        date: record.date ? DateTime.fromISO(record.date) : undefined,
        durationInMinutes: record.durationInMinutes,
      });
    }
    return acc;
  }, [] as Array<TimesheetEntry>);
}
